import type { QuotationLineItem } from "@/mocks/quotationsData";
import { computeSubtotal, computeTaxTotal, computeTotal, companyInfo as defaultCompanyInfo } from "@/mocks/quotationsData";

type Props = {
  quoteNo: string;
  date: string;
  validUntil: string;
  leadName: string;
  leadPhone: string;
  leadEmail: string;
  items: QuotationLineItem[];
  discount: number;
  terms: string;
  agent: string;
  companyInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstin?: string;
  };
};

export function QuotationPreview({
  quoteNo,
  date,
  validUntil,
  leadName,
  leadPhone,
  leadEmail,
  items,
  discount,
  terms,
  agent,
  companyInfo = defaultCompanyInfo
}: Props) {
  const company = { ...defaultCompanyInfo, ...companyInfo };
  const subtotal = computeSubtotal(items);
  const taxTotal = computeTaxTotal(items);
  const total = computeTotal(items, discount);

  const formatDate = (d: string) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Preview header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Preview</span>
        <span className="text-[10px] text-gray-400">Live</span>
      </div>

      <div className="p-4 space-y-3 scale-[0.85] origin-top-left w-[117%]">
        {/* Company + Quotation header */}
        <div className="flex justify-between items-start">
          <div>
            <div className="w-8 h-8 rounded bg-indigo-600 flex items-center justify-center mb-1">
              <span className="text-white text-[8px] font-bold">
                {company.name.slice(0, 2).toUpperCase()}
              </span>
            </div>
            <p className="text-[10px] font-semibold text-gray-900">{company.name}</p>
            <p className="text-[8px] text-gray-500 leading-relaxed">{company.address}</p>
            <p className="text-[8px] text-gray-500">{company.phone} | {company.email}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold text-gray-900 tracking-wide">QUOTATION</p>
            <p className="text-[10px] font-semibold text-indigo-600">{quoteNo}</p>
            <p className="text-[8px] text-gray-500 mt-0.5">Date: {formatDate(date)}</p>
            <p className="text-[8px] text-gray-500">Valid Until: {formatDate(validUntil)}</p>
          </div>
        </div>

        {/* To section */}
        <div className="border-t border-gray-100 pt-2">
          <p className="text-[8px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">To</p>
          <p className="text-[10px] font-semibold text-gray-900">{leadName || "—"}</p>
          {leadPhone && <p className="text-[8px] text-gray-500">{leadPhone}</p>}
          {leadEmail && <p className="text-[8px] text-gray-500">{leadEmail}</p>}
        </div>

        {/* Items mini table */}
        <div className="border-t border-gray-100 pt-2">
          <div className="flex text-[7px] font-semibold text-gray-400 uppercase tracking-wider pb-1 border-b border-gray-100">
            <span className="flex-1">Description</span>
            <span className="w-8 text-center">Qty</span>
            <span className="w-16 text-right">Amount</span>
          </div>
          {items.slice(0, 3).map((item) => {
            const amt = item.qty * item.rate * (1 + item.taxPercent / 100);
            return (
              <div key={item.id} className="flex text-[8px] py-1 border-b border-gray-50">
                <span className="flex-1 text-gray-900 truncate pr-1">{item.description || "Untitled item"}</span>
                <span className="w-8 text-center text-gray-600">{item.qty}</span>
                <span className="w-16 text-right text-gray-900 font-medium">₹{amt.toLocaleString("en-IN")}</span>
              </div>
            );
          })}
          {items.length > 3 && (
            <p className="text-[8px] text-gray-400 italic mt-1">+{items.length - 3} more items...</p>
          )}
        </div>

        {/* Total box */}
        <div className="bg-indigo-600 rounded-lg p-2.5 flex justify-between items-center">
          <span className="text-[9px] font-semibold text-white/80">TOTAL</span>
          <span className="text-sm font-bold text-white">₹{total.toLocaleString("en-IN")}</span>
        </div>

        {/* Terms snippet */}
        {terms && (
          <div className="border-t border-gray-100 pt-2">
            <p className="text-[7px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Terms</p>
            <p className="text-[7px] text-gray-500 line-clamp-2 leading-relaxed">{terms.split("\n")[0]}</p>
          </div>
        )}

        {/* Agent */}
        <div className="text-right pt-1">
          <p className="text-[8px] text-gray-400">Prepared by: <span className="text-gray-600 font-medium">{agent}</span></p>
        </div>
      </div>
    </div>
  );
}